import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { Modal } from '@hdi-seguros/web-components.modal';
import { Form as FormAnt, ModalProps, message } from "antd";

import { InsurancePolicy, SelectOption, UserInformation } from "../../../../../shared/interfaces";
import { Column, _renderColumns } from "../../utils/renderSections";

import dayjs, { Dayjs } from "dayjs";
import { ColSize, DatePickerForm, Form, RowSize, SelectForm } from "../../../../../shared/components";
import { AuthContext, UIContext } from "../../../../../shared/contexts";
import { CancelPolicyRequest } from "../../../../../shared/interfaces/services/cancel-policy-request";
import MasterService from "../../../../../shared/services/master";
import ProducersService from "../../../../../shared/services/producers";
import { ValidateForm, displaySuccessMessage, getUserInfo, renderCurrency } from "../../../../../shared/utils";
import './index.css';
import { CancellationMethod } from "../../../../../shared/interfaces/services/get-cancellation-method-service";

interface CancelPolicyModalProps extends ModalProps {
    insurancePolicy?: InsurancePolicy;
    onClose: () => void;
    onUpdate: () => void;
}

export const CancelPolicyModal: FC<CancelPolicyModalProps> = ({ open, title, onClose,onUpdate, insurancePolicy }) => {

    const { id } = useParams();
    const authContext = useContext(AuthContext);
    const user = authContext.userInfo as UserInformation;
    const [form] = FormAnt.useForm();
    const [loading, setLoading] = useState(false);
    const validateForm = new ValidateForm();
    const {setIsLoading, setShowError } = useContext(UIContext);
    const [reasonList, setReasonList] = useState<SelectOption[]>([]);
    const [methodsList, setMethodsList] = useState<CancellationMethod[]>([]);
    const [methodsOptions, setMethodsOptions] = useState<SelectOption[]>([]);
    const [isDateDisabled, setIsDateDisabled] = useState<boolean>(true);
    const [cancelDateObligaroryValue, setCancelDateObligaroryValue] = useState<Dayjs>();
    const getCancellationReason = useCallback(async () => {

        setShowError(false);
        try {
            const response = await MasterService.getListCancellationReason(insurancePolicy?.articulo ?? "", user.usuarioInterno);
            setReasonList(response);
        } catch (error) {
            setShowError(true);
        }
    }, [setIsLoading, setShowError, setReasonList]);

    const getCancellationMethod = useCallback(
        async (reason: number) => {
            setShowError(false);
            try {
                const response = await MasterService.getListCancellationMethod(
                    insurancePolicy?.articulo ?? "",
                    user.usuarioInterno,
                    reason.toString(),
                );
                setMethodsList(response);
                const data = response.map((reason: CancellationMethod) => {
                    return {
                      value: reason.codigo,
                      label: reason.descripcion,
                    };
                  });
                setMethodsOptions(data)
            } catch (error) {
                setShowError(true);
            }
        },
        [insurancePolicy, setShowError, setReasonList]
    );

    const handleChangeCancellationMethod = useCallback((value: string | number) => {        
        const selectedMethod = methodsList.find((method) => String(method.codigo) === String(value));
        const shouldDisableDate = selectedMethod?.permiteModificarFecha === "N";        
        if(shouldDisableDate){            
            const parsed = dayjs(selectedMethod!.fechaDeAnulacion, "YYYY-MM-DD");
            setCancelDateObligaroryValue(parsed);
            form.setFieldsValue({ cancelDate: parsed });
            setIsDateDisabled(Boolean(shouldDisableDate));
        }else{
            form.resetFields(["cancelDate"]);
        }
    }, [methodsList]);

    const insuranceColumns: Array<Column> = useMemo(() => [
        {
            label: 'Descripción de la Rama',
            value: insurancePolicy?.descripcionRama
        },
        {
            label: 'Número de Póliza',
            value: insurancePolicy?.nroPoliza,
        },
        {
            label: 'Nombre del Asegurado',
            value: insurancePolicy?.nombreAsegurado,
        },
        {
            label: 'Vigencia Desde',
            value: insurancePolicy?.fechaVigDesd ? dayjs(insurancePolicy?.fechaVigDesd)
                .format('DD/MM/YYYY')
                .split('-')
                .join('/') : "-",
        },
        {
            label: 'Vigencia Hasta',
            value: insurancePolicy?.fechaVigHast ? dayjs(insurancePolicy?.fechaVigHast)
                .format('DD/MM/YYYY')
                .split('-')
                .join('/') : "-",
        },
        {
            label: 'Saldo',
            value: insurancePolicy?.saldoPendiente ? renderCurrency(insurancePolicy?.saldoPendiente) : renderCurrency(0.00),
        }
    ], [id, insurancePolicy]);

    const onSubmit = async () => {
        try {
            await form.validateFields();
            form.submit();
        } catch (error) { }
    };


    const onCancelPolicy = async () => {
        setLoading(true);
        const cancelDate = dayjs(form.getFieldValue('cancelDate'));

        const cancelPolicyRequest: CancelPolicyRequest = {
            base: getUserInfo(),
            articulo: Number(insurancePolicy!.articulo),
            superpoliza: Number(insurancePolicy!.superpoliza),
            motivo: Number(form.getFieldValue('reason')),
            formaDeAnular: Number(form.getFieldValue('method')),
            fechaDeAnulacion: cancelDate.format("DD-MM-YYYY"),
        }

        try {
            message.info("Anulando la póliza. Por favor espere un momento sin cerrar esta pantalla");
            const response = await ProducersService.postExecuteCancellation(cancelPolicyRequest);
            if (response.data.numeroPropuesta) {
                const messageSuccess: string = `La póliza ha sido anulada. El número de solicitud es: ${response.data.numeroPropuesta}`;
                displaySuccessMessage(messageSuccess);
                onUpdate();
            }else{
                message.warning(`Número de solicitud no asignado al anular la póliza ${id}`);
                onClose();
            }
        } catch (error) {
            message.error(`Hubo un error intentando anular la poliza ${id}`);
            onClose();
        }
    }

    const getComboInfo = useCallback(() => {
        Promise.all([getCancellationReason()])
            .then(() => {
                
            })
            .catch(error => { setShowError(true) });
    }, [getCancellationReason]);

    useEffect(() => {
        getComboInfo();
    }, []);


    return (
        <Modal
            title={title}
            centered
            open={open}
            width={650}
            onOk={onSubmit}
            onCancel={onClose}
            confirmText={'Anular'}
            footer
            loadingConfirmButton={loading}
            className="cancel-policy-modal-container"
        >
            <>
                <RowSize rowProps={{ gutter: [0, 15], justify: 'start' }} className="row-insurance-cancel-policy">
                    {_renderColumns(insuranceColumns)}
                </RowSize>

                <Form
                    formProps={{
                        form,
                        name: "cancellation-digitial-policy-form",
                        onLoad: getCancellationReason,
                        onFinish: onCancelPolicy,
                        style: { marginBottom: 10, marginTop: 10 },
                    }}
                >
                    <RowSize rowProps={{ gutter: [15, 15], justify: 'start' }} className="row-insurance-cancel-policy" >
                        <ColSize size={25} >
                            <SelectForm
                                formProps={{
                                    label: "Motivo:",
                                    name: "reason",
                                    rules: validateForm.generateRules({
                                        label: "Motivo",
                                        rules: ["required"],
                                    }),
                                }}
                                selectProps={{
                                    onChange: (reason) => {
                                        getCancellationMethod(reason);
                                    },
                                    loading: reasonList?.length === 0,
                                    disabled: reasonList?.length === 0,
                                    options: reasonList,
                                    showSearch: true,
                                }}
                            />
                        </ColSize>
                        <ColSize size={25} >
                            <SelectForm
                                formProps={{
                                    label: "Forma de Anulación:",
                                    name: "method",
                                    rules: validateForm.generateRules({
                                        label: "Forma",
                                        rules: ["required"],
                                    }),
                                }}
                                selectProps={{
                                    onChange: handleChangeCancellationMethod,
                                    loading: methodsOptions?.length === 0,
                                    disabled: methodsOptions?.length === 0,
                                    options: methodsOptions,
                                    showSearch: true,
                                }}
                            />
                        </ColSize>
                        <ColSize size={25}>
                            <DatePickerForm
                                formProps={{
                                    label: "Fecha",
                                    required: true,
                                    name: "cancelDate",
                                    rules: validateForm.generateRules({
                                        label: "fecha",
                                        rules: ["required"],
                                    }),
                                    initialValue: isDateDisabled ? cancelDateObligaroryValue : undefined,
                                }}
                                datePickerProps={{
                                    placeholder: dayjs().format("DD/MM/YYYY"),
                                    disabledDate: (current) => current && current > dayjs().endOf('day'),
                                    disabled: isDateDisabled
                                }}
                            />
                        </ColSize>
                    </RowSize>
                </Form>
            </>
        </Modal>
    )
}
